import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'

/**
 * Access token-ul se ține doar în memorie (FR-ADM-001b, decizia de securitate din
 * §5.1): în localStorage ar fi citibil de orice script injectat. Refresh token-ul
 * nu trece niciodată prin JavaScript — stă în cookie HttpOnly.
 */
let accessToken: string | null = null
let onSessionLost: (() => void) | null = null

export const tokenStore = {
  get: () => accessToken,
  set: (t: string | null) => { accessToken = t },
  onLost: (cb: () => void) => { onSessionLost = cb },
}

export const api = axios.create({
  baseURL: '/api',
  withCredentials: true, // necesar pentru cookie-ul de refresh
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`
  return config
})

/**
 * Reîmprospătare transparentă (FR-ADM-001h): expirarea access token-ului nu
 * deconectează utilizatorul și nu pierde formularul deschis. Cererile care
 * primesc 401 sunt puse în așteptare, se face un singur refresh, apoi se reiau.
 * Reluarea se face o singură dată — altfel un 401 persistent ar bucla la infinit.
 */
let refreshing: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  try {
    const { data } = await axios.post<{ accessToken: string }>(
      '/api/auth/refresh', {}, { withCredentials: true },
    )
    accessToken = data.accessToken
    return accessToken
  } catch {
    accessToken = null
    onSessionLost?.()
    return null
  } finally {
    refreshing = null
  }
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean }

    if (error.response?.status !== 401 || original._retried || original.url?.includes('/auth/')) {
      return Promise.reject(error)
    }

    original._retried = true
    refreshing ??= refreshAccessToken()
    const fresh = await refreshing

    if (!fresh) return Promise.reject(error)
    original.headers.Authorization = `Bearer ${fresh}`
    return api(original)
  },
)

/** Mesaj de eroare afișabil, extras din ProblemDetails sau din răspunsul de auth. */
export function errorMessage(e: unknown, fallback = 'A apărut o eroare neașteptată.'): string {
  if (axios.isAxiosError(e)) {
    const d = e.response?.data as { message?: string; detail?: string; title?: string } | undefined
    return d?.message ?? d?.detail ?? d?.title ?? (e.message || fallback)
  }
  return fallback
}
